/**
 * SERAF — Cadeau automatique panier (seuil 200 CHF de produits expédiables)
 * Écoute cart:updated (Fullstack), sync via Cart AJAX, message drawer.
 */
(function () {
  const THRESHOLD_CENTS = 20000;
  const GIFT_HANDLE = 'cadeau-surprise';
  const GIFT_TAG = 'cadeau-auto';
  const SOURCE = 'seraf-gift-cart';
  const CART_UPDATED = 'cart:updated';

  let giftVariantId = null;
  let giftVariantPromise = null;
  let syncQueue = Promise.resolve();
  let isSyncing = false;

  function getCartDrawerSectionId() {
    return document.querySelector('cart-drawer')?.dataset?.sectionId || null;
  }

  function isGiftLine(item) {
    if (!item) return false;
    if (item.handle === GIFT_HANDLE) return true;
    if (Array.isArray(item.tags) && item.tags.includes(GIFT_TAG)) return true;
    if (typeof item.url === 'string' && item.url.indexOf('/products/' + GIFT_HANDLE) !== -1) return true;
    return false;
  }

  function getEligibleSubtotalCents(cart) {
    if (!cart?.items?.length) return 0;
    return cart.items.reduce(function (sum, item) {
      if (isGiftLine(item)) return sum;
      // Les cartes cadeaux et autres produits numériques ne doivent jamais
      // déclencher un cadeau physique, sinon Shopify réclame des frais de port.
      if (item.gift_card === true || item.requires_shipping === false) return sum;
      return sum + (item.final_line_price || 0);
    }, 0);
  }

  function getGiftLines(cart) {
    if (!cart?.items?.length) return [];
    return cart.items.filter(isGiftLine);
  }

  function fetchCart() {
    return fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then(function (res) {
      if (!res.ok) throw new Error('cart.js failed');
      return res.json();
    });
  }

  function resolveGiftVariantId() {
    if (giftVariantId) return Promise.resolve(giftVariantId);
    if (giftVariantPromise) return giftVariantPromise;

    giftVariantPromise = fetch('/products/' + GIFT_HANDLE + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (res) {
        if (!res.ok) throw new Error('gift product not found');
        return res.json();
      })
      .then(function (product) {
        var variant = product.variants && product.variants[0];
        if (!variant?.id) throw new Error('gift variant missing');
        giftVariantId = variant.id;
        return giftVariantId;
      })
      .catch(function (err) {
        giftVariantPromise = null;
        console.warn('[seraf-gift-cart]', err.message);
        return null;
      });

    return giftVariantPromise;
  }

  function postCartJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }).then(function (res) {
      return res.json();
    });
  }

  function dispatchCartUpdated(payload) {
    document.dispatchEvent(
      new CustomEvent(CART_UPDATED, {
        bubbles: true,
        detail: {
          resource: {},
          data: payload,
        },
      }),
    );
  }

  function refreshDrawerSections(response) {
    var sectionId = getCartDrawerSectionId();
    if (!sectionId || !response?.sections?.[sectionId]) return;

    dispatchCartUpdated({
      itemCount: response.item_count,
      source: SOURCE,
      sections: response.sections,
    });
  }

  function buildSectionsPayload() {
    var sectionId = getCartDrawerSectionId();
    if (!sectionId) return {};
    return {
      sections: sectionId,
      sections_url: window.location.pathname,
    };
  }

  function addGift(variantId) {
    var payload = Object.assign(
      {
        items: [{ id: variantId, quantity: 1, properties: { Cadeau: 'Surprise dès 200 CHF' } }],
      },
      buildSectionsPayload(),
    );
    return postCartJson(window.Theme?.routes?.cart_add_url || '/cart/add.js', payload);
  }

  function changeGiftQuantity(lineKeyOrId, quantity) {
    var payload = Object.assign(
      {
        quantity: quantity,
      },
      buildSectionsPayload(),
    );

    if (typeof lineKeyOrId === 'number') {
      payload.line = lineKeyOrId;
    } else if (lineKeyOrId) {
      payload.id = lineKeyOrId;
    }

    return postCartJson(window.Theme?.routes?.cart_change_url || '/cart/change', payload);
  }

  function updateGiftMessage(cart) {
    var nodes = document.querySelectorAll('[data-ref="seraf-gift-cart-message"]');
    if (!nodes.length) return;

    var eligible = getEligibleSubtotalCents(cart);
    var gifts = getGiftLines(cart);
    var hasGift = gifts.length > 0;

    nodes.forEach(function (node) {
      node.classList.remove('seraf-gift-cart-message--success', 'seraf-gift-cart-message--pending', 'seraf-gift-cart-message--hidden');

      if (!cart?.items?.length) {
        node.textContent = '';
        node.classList.add('seraf-gift-cart-message--hidden');
        return;
      }

      if (hasGift || eligible >= THRESHOLD_CENTS) {
        node.textContent = 'Ton cadeau surprise est dans le panier 🎁';
        node.classList.add('seraf-gift-cart-message--success');
        return;
      }

      node.textContent = '';
      node.classList.add('seraf-gift-cart-message--hidden');
    });
  }

  function syncGiftCart(cart) {
    return resolveGiftVariantId().then(function (variantId) {
      if (!variantId) return cart;

      var eligible = getEligibleSubtotalCents(cart);
      var shouldHaveGift = eligible >= THRESHOLD_CENTS;
      var giftLines = getGiftLines(cart);

      if (giftLines.length > 1) {
        var lastResponse = null;
        var removeExtras = giftLines.slice(1).reduce(function (chain, item) {
          return chain.then(function () {
            return changeGiftQuantity(item.key, 0).then(function (response) {
              lastResponse = response;
            });
          });
        }, Promise.resolve());

        return removeExtras.then(function () {
          if (lastResponse) refreshDrawerSections(lastResponse);
          return fetchCart().then(syncGiftCart);
        });
      }

      if (giftLines.length === 1 && giftLines[0].quantity > 1) {
        return changeGiftQuantity(giftLines[0].key, 1).then(function (response) {
          refreshDrawerSections(response);
          return fetchCart();
        });
      }

      if (shouldHaveGift && giftLines.length === 0) {
        return addGift(variantId).then(function (response) {
          if (response.status === 422 || response.description) {
            console.warn('[seraf-gift-cart]', response.description || response.message);
            return cart;
          }
          refreshDrawerSections(response);
          return fetchCart();
        });
      }

      if (!shouldHaveGift && giftLines.length > 0) {
        return changeGiftQuantity(giftLines[0].key, 0).then(function (response) {
          refreshDrawerSections(response);
          return fetchCart();
        });
      }

      return cart;
    });
  }

  var lastGiftAttr = null;
  function syncGiftAttribute(cart) {
    if (!cart) return Promise.resolve(cart);
    var shouldHave = getEligibleSubtotalCents(cart) >= THRESHOLD_CENTS;
    var next = shouldHave ? 'oui' : '';
    var current = (cart.attributes && cart.attributes['Cadeau 200 CHF']) || '';
    if (current === next || lastGiftAttr === next) {
      lastGiftAttr = next;
      return Promise.resolve(cart);
    }
    lastGiftAttr = next;
    return postCartJson(window.Theme?.routes?.cart_update_url || '/cart/update.js', {
      attributes: { 'Cadeau 200 CHF': next },
    }).then(function () {
      return fetchCart();
    });
  }

  function runSync() {
    syncQueue = syncQueue
      .then(function () {
        if (isSyncing) return null;
        isSyncing = true;
        return fetchCart();
      })
      .then(function (cart) {
        if (!cart) return null;
        updateGiftMessage(cart);
        return syncGiftCart(cart);
      })
      .then(function (cart) {
        if (!cart) return null;
        return syncGiftAttribute(cart);
      })
      .then(function (cart) {
        if (cart) updateGiftMessage(cart);
      })
      .catch(function (err) {
        console.warn('[seraf-gift-cart]', err);
      })
      .finally(function () {
        isSyncing = false;
      });

    return syncQueue;
  }

  document.addEventListener(CART_UPDATED, function (event) {
    var source = event.detail?.data?.source;

    if (source === SOURCE) {
      fetchCart().then(updateGiftMessage).catch(function () {});
      return;
    }

    runSync();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runSync);
  } else {
    runSync();
  }
})();
